// Price-history-derived momentum factors. Extracted from factor-pipeline.ts
// to keep that file under the 500-line god-file threshold.
//
//   momentum         = 12-1 momentum (Jegadeesh-Titman) — 1-month-ago price /
//                      12-month-ago price - 1. The 1-month skip avoids the
//                      short-term-reversal signal contaminating the trend read.
//   nearHigh         = current price / 52-week high — proximity to the cycle high.
//   shortTermReversal = 1-month return (negative of momentum at short horizon —
//                       inverted at scoring time because recent losers bounce).

import type { StockData } from '../types';

function safe(n: number | undefined | null): number | null {
  if (n == null || !isFinite(n) || isNaN(n)) return null;
  return n;
}

export interface PriceMomentumFactors {
  momentum: number | null;
  nearHigh: number | null;
  shortTermReversal: number | null;
}

export function computePriceMomentum(d: StockData): PriceMomentumFactors {
  let momentum: number | null = null;
  let nearHigh: number | null = null;
  let shortTermReversal: number | null = null;

  if (!d.priceHistory || d.priceHistory.length === 0 || !d.price || d.price <= 0) {
    return { momentum, nearHigh, shortTermReversal };
  }

  const sorted = [...d.priceHistory].sort((a, b) => b.date.localeCompare(a.date));
  const now = new Date();
  const target1mo = new Date(now); target1mo.setMonth(target1mo.getMonth() - 1);
  const target12mo = new Date(now); target12mo.setMonth(target12mo.getMonth() - 12);

  let price1mo: number | null = null;
  let price12mo: number | null = null;
  let best1 = Infinity, best12 = Infinity;
  for (const p of sorted) {
    const diff1 = Math.abs(new Date(p.date).getTime() - target1mo.getTime());
    const diff12 = Math.abs(new Date(p.date).getTime() - target12mo.getTime());
    if (diff1 < best1) { best1 = diff1; price1mo = p.price; }
    if (diff12 < best12) { best12 = diff12; price12mo = p.price; }
  }
  if (price1mo && price12mo && price12mo > 0) {
    momentum = safe(price1mo / price12mo - 1);
  }
  if (price1mo && price1mo > 0) {
    shortTermReversal = safe(d.price / price1mo - 1);
  }

  const yearMax = Math.max(...d.priceHistory.map(p => p.price));
  if (yearMax > 0) nearHigh = safe(d.price / yearMax);

  return { momentum, nearHigh, shortTermReversal };
}
